
import React, { createContext, useContext, useState } from 'react';
import { Outlet } from 'react-router-dom';
import useNavigation from '../../hooks/useNavigation';
import { validateSocialHandle } from '../../utils/socialHandleValidation';

// export type TalentAccountTypeId = 'individual' | 'company';

export type CreatorAccounetTypeId = 'brand' | 'creative_agency' | 'personal';

export type UserType = 'creator' | 'influencer' | 'artist';

// export type AccountType = TalentAccountTypeId | CreatorAccountTypeId;
export type AccountType = 'personal' | 'business' | 'agency' | 'brand' | 'creative_agency' | 'creative_agency' | 'company' | 'individual'
  ;

export type LicenseID = 'personal' | 'standard' | 'commercial';

export interface SocialDetails {
  followers: number | null;
  handle: string | null;


}

export interface SocialNetworks {
  facebook: SocialDetails;
  instagram: SocialDetails;
  tiktok: SocialDetails;
  x?: SocialDetails;
  twitter?: SocialDetails;
}

export type ArtirtPricingModel = 'own' | 'saagas' | undefined;

export interface Account {
  userType?: UserType;
  accountType?: AccountType;
  accountName: string;
  companyName?: string;
  profession?: string;
  categories?: string[];
  contentOptions?: string[];
  about: string;
  collaborations?: string[];
  coverphoto: string;
  coverImage: string;
  headshot: string;
  socialNetworks: SocialNetworks;
  website: string | null;
  pricing: Record<string, number>;
  artist: {
    styleName: string;
    pricing: {
      model: string;
      price: number;
    };
  };
  published?: boolean;
  licenses?: {
    type: string;
    price: string;
  }[];
  artstyleProfileId?: string;
}

export const initialAccount: Account = {
  accountType: 'personal',
  accountName: '',
  companyName: '',
  profession: '',
  about: '',
  categories: [],
  contentOptions: [],
  coverphoto: '',
  coverImage: '',
  headshot: '',
  socialNetworks: {
    facebook: {
      followers: null,
      handle: ''
    },
    instagram: {
      followers: null,
      handle: ''
    },
    tiktok: {
      followers: null,
      handle: ''
    }
  },
  website: '',
  pricing: {
    personal: 0,
    standard: 0,
    commercial: 0
  },
  artist: {
    styleName: '',
    pricing: {
      model: '',
      price: 0
    }
  },
  published: false,
  licenses: [
    {
      type: 'personal',
      price: '0'
    }
  ]
};


interface SignupContextProps {
  account: Account;
  setAccount: React.Dispatch<React.SetStateAction<Account>>;
}

export const isFormComplete = (account) => {
  const isFormDataValid = validateSocialAccountsForm(account);
  const hasRequiredData = hasRequiredSocials(account);
  return isFormDataValid && hasRequiredData;
};


export const validateSocialAccountsForm = (account: Account): boolean => {
  const websitePattern = /^https?:\/\/(?:www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(?:\/[^\s]*)?$/;

  const { socialNetworks, website } = account;

  // Validate handles using platform-specific validation
  const handleEntries = [
    { platform: 'facebook' as const, handle: socialNetworks?.facebook?.handle },
    { platform: 'instagram' as const, handle: socialNetworks?.instagram?.handle },
    { platform: 'tiktok' as const, handle: socialNetworks?.tiktok?.handle }
  ];

  // Check each handle that has a value
  for (const { platform, handle } of handleEntries) {
    const trimmedHandle = handle?.trim();
    if (trimmedHandle && trimmedHandle.length > 0) {
      const validation = validateSocialHandle(platform, trimmedHandle);
      if (!validation.isValid) {
        return false;
      }
    }
  }

  // Validate website if provided
  const isValidWebsite = (website?: string): boolean => !website || websitePattern.test(website.trim());
  return isValidWebsite(website);
};

export const hasRequiredSocials = (account: Account): boolean => {
  if (!account) return false;

  const { socialNetworks } = account;

  const socialHandles = [
    socialNetworks?.facebook?.handle,
    socialNetworks?.instagram?.handle,
    socialNetworks?.tiktok?.handle
  ];

  // Count only valid handles
  const entries = socialHandles.filter((it) => { return !!it; }).length;

  if (account.userType === 'influencer') {
    return entries > 0;
  }
  return entries > 0 || !!account.website;
};

const SignupContext = createContext<SignupContextProps | undefined>(undefined);

export const SignupProvider = ({ children }: { children?: React.ReactNode }) => {

  const [account, setAccount] = useState<Account>(initialAccount);
  const navigation = useNavigation();

  if (navigation) return navigation;

  return (
    <SignupContext.Provider value={{ account, setAccount }}>
      {children || <Outlet />}
    </SignupContext.Provider>
  );
};

export const useSignup = (): SignupContextProps => {
  const context = useContext(SignupContext);
  if (!context) {
    throw new Error('useSignup must be used within an SignupProvider');
  }
  return context;
};
